import objectAssign = require('object-assign');
import objectMerge = require('object-merge');

interface IXMLData
{
    data: Data;
}

interface Data
{
    config?: (string)[] | null;
    globalContent?: (GlobalContentEntity)[] | null;
    slide?: (any)[] | null;
}

interface GlobalContentEntity
{
    dataPart?: DataPart[] | null;
}

interface DataPart
{
    $: {
        id: string;
        type: string;
    }
}

interface ContentElementWhichShouldBeInjected
{
    $: {
        useGlobalPageID?: string
    }
}

interface ElementWhichCanBePreloaded
{
    $: {
        src: string,
        preload: boolean,
        id?:string,
    }
}

interface ElementWhichHaveHrefAttribute
{
    $: {
        url: string,
    }
}

export class ContentDataHelper
{
    /**
     * Search for slide node with given ID and returns object if found, otherwise null
     * @param {string} id
     * @param {IXMLData} fileData
     * @returns {any}
     */
    static getDataSlideByID(id: string, fileData: IXMLData): any
    {
        if (fileData && fileData.data && fileData.data.slide)
        {
            for (let i: number = 0; i < fileData.data.slide.length; i++)
            {
                if (fileData.data.slide && fileData.data.slide[i].$.id == id)
                {
                    return fileData.data.slide[i];
                }
            }
        }
        return null;
    }

    /**
     * Helper which will replace nodes by global node elements
     *
     * @param fileData
     * @returns {IXMLData}
     */
    static injectGlobalElements(fileData: any): IXMLData
    {
        const data: IXMLData = objectAssign({}, fileData) as IXMLData;
        const globalElements: any = {};
        // inject the global elements
        data.data.globalContent.map((content: GlobalContentEntity, index: number) =>
        {
            // search for global page tabs
            if(content.dataPart)
            {
                content.dataPart.map((content: DataPart, index: number) =>
                {
                    globalElements[content.$.id] = content;
                });
            }
        });

        return this.injectGlobalPageContents(data, globalElements);
    }

    /**
     * Helper which injects global page content data
     *
     * @param {IXMLData} fileData
     * @param {"../index".Dictionary<DataPart>} globalElements
     * @returns {IXMLData}
     */
    private static injectGlobalPageContents(fileData: IXMLData, globalElements: any): IXMLData
    {
        for (let key in fileData)
        {
            let value = fileData[key];

            // recursive cycle
            if ((value && value instanceof Object) || Array.isArray(value))
            {
                this.injectGlobalPageContents(value, globalElements);
            }

            // we test if object needs to be merged by typecast test and ID check
            let typedObject: ContentElementWhichShouldBeInjected = value as ContentElementWhichShouldBeInjected;
            if (typedObject && typedObject.$ && typedObject.$.useGlobalPageID)
            {
                if (globalElements[typedObject.$.useGlobalPageID])
                {
                    // duplicate object
                    let newLinkedObject: any = objectAssign({}, globalElements[typedObject.$.useGlobalPageID]);
                    // now (deep) merge the fresh object, with the still existing object
                    objectMerge(value, value, newLinkedObject);
                }
            }

        }

        return fileData;
    }

    /**
     * Returns srcs of objects which sould be preloaded
     *
     * @param fileData
     * @returns {string[]}
     */
    static getSrcsToBePreloaded(fileData: any): string[]
    {
        const data: IXMLData = fileData as IXMLData;
        let srcsToBePreloaded: string[] = [];

        this.elementsToBePreloadedRecursiveSearch(data, srcsToBePreloaded);

        return srcsToBePreloaded;
    }

    /**
     * Deep search for elements which should be preloaded
     *
     * @param {IXMLData} fileData
     * @param {string[]} srcsToBePreloaded
     */
    private static elementsToBePreloadedRecursiveSearch(fileData: IXMLData, srcsToBePreloaded: string[]): void
    {
        for (let key in fileData)
        {
            let value = fileData[key];

            // recursive cycle
            if ((value && value instanceof Object) || Array.isArray(value))
            {
                this.elementsToBePreloadedRecursiveSearch(value, srcsToBePreloaded);
            }

            // we test if object needs to be merged by typecast test and ID check
            let typedObject: ElementWhichCanBePreloaded = value as ElementWhichCanBePreloaded;
            if (typedObject && typedObject.$ && typedObject.$.src && typedObject.$.preload)
            {
                // avoid double referencing
                if (srcsToBePreloaded.indexOf(typedObject.$.src) == -1)
                {
                    srcsToBePreloaded.push(typedObject.$.src);

                    if(typedObject.$.id)
                    {
                        this._preloadedAssetsWithIDs[typedObject.$.id] = typedObject.$.src;
                    }
                }
            }

        }
    }

    private static _preloadedAssetsWithIDs:any = {};

    /**
     * Returns URL of asset in case asset has been detected by getSrcsToBePreloaded.
     * @param {string} id
     * @returns {string}
     */
    public static getPreloadedAssetURIByID(id:string):string
    {
        return this._preloadedAssetsWithIDs[id];
    }

    /**
     * Returns an array with all found internal urls
     *
     * @param fileData
     * @returns {string[]}
     */
    static getListOfURLs(fileData: any): string[]
    {
        const data: IXMLData = fileData as IXMLData;
        let urls: string[] = [];

        this.searchForURLsRecursive(data, urls);

        return urls;
    }

    /**
     * Deep search for href attributes
     *
     * @param {IXMLData} fileData
     * @param {string[]} urls
     */
    private static searchForURLsRecursive(fileData: IXMLData, urls: string[]): void
    {
        for (let key in fileData)
        {
            let value = fileData[key];

            // recursive cycle
            if ((value && value instanceof Object) || Array.isArray(value))
            {
                this.searchForURLsRecursive(value, urls);
            }

            // we test if object needs to be merged by typecast test and ID check
            let typedObject: ElementWhichHaveHrefAttribute = value as ElementWhichHaveHrefAttribute;
            if (typedObject && typedObject.$ && typedObject.$.url)
            {
                // avoid double referencing
                if (urls.indexOf(typedObject.$.url) == -1)
                {
                    urls.push(typedObject.$.url);
                }
            }

        }
    }
}